import {skipToken, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {dispatchKeys} from 'src/dispatch/query-keys'
import {jobKeys} from 'src/jobs/query-keys'
import {invalidateEquipmentMoves} from 'src/project-deployed-equipment/invalidate-equipment-moves'
import {projectKeys} from 'src/projects/query-keys'
import {activeRentalKeys} from 'src/rentals/query-keys'

import {
    confirmPickupReady,
    confirmPickupReturns,
    createPickupJob,
    listCustomerWork,
    listPickupCandidates,
    listPickupsToConfirm,
    listPickupUnits,
    pickupSooner,
} from './api'
import {workKeys} from './query-keys'
import type {ConfirmPickupReturnsInput, CreatePickupJobInput, PickupSoonerInput} from './schemas'

export function useCustomerWork(
    customerId: string | null,
    {
        includeClosedWork = false,
        enabled = true,
    }: {includeClosedWork?: boolean; enabled?: boolean} = {},
) {
    return useQuery({
        queryKey: workKeys.customer(customerId ?? '', includeClosedWork),
        queryFn: customerId ? () => listCustomerWork(customerId, includeClosedWork) : skipToken,
        enabled,
    })
}

export function usePickupCandidates(projectId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: workKeys.pickupCandidates(projectId),
        queryFn: () => listPickupCandidates(projectId),
        enabled: options?.enabled ?? Boolean(projectId),
    })
}

export function usePickupUnits(projectId: string, jobId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: workKeys.pickupUnits(projectId, jobId),
        queryFn: () => listPickupUnits(projectId, jobId),
        enabled: options?.enabled ?? Boolean(projectId && jobId),
    })
}

function useInvalidateWork(projectId: string) {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({queryKey: workKeys.all})
        void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
        void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
        void queryClient.invalidateQueries({queryKey: activeRentalKeys.all})
        void queryClient.invalidateQueries({queryKey: jobKeys.all})
        void queryClient.invalidateQueries({queryKey: dispatchKeys.drivers()})
    }
}

export function usePickupsToConfirm(yardId: string | null) {
    return useQuery({
        queryKey: workKeys.pickupsToConfirm(yardId),
        queryFn: () => listPickupsToConfirm(yardId),
        refetchOnWindowFocus: 'always',
    })
}

export function useCreatePickupJob(projectId: string) {
    const invalidate = useInvalidateWork(projectId)
    return useMutation({
        mutationFn: (input: CreatePickupJobInput) => createPickupJob(projectId, input),
        onSuccess: invalidate,
    })
}

export function useConfirmPickupReturns(projectId: string, jobId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: ConfirmPickupReturnsInput) =>
            confirmPickupReturns(projectId, jobId, input),
        onSuccess: () => {
            void invalidateEquipmentMoves(queryClient, projectId)
            void queryClient.invalidateQueries({queryKey: dispatchKeys.drivers()})
        },
    })
}

export function usePickupSooner(projectId: string) {
    const invalidate = useInvalidateWork(projectId)
    return useMutation({
        mutationFn: (input: PickupSoonerInput) => pickupSooner(projectId, input),
        onSuccess: invalidate,
    })
}

export function useConfirmPickupReady(projectId: string, jobId: string) {
    const invalidate = useInvalidateWork(projectId)
    return useMutation({
        mutationFn: () => confirmPickupReady(projectId, jobId),
        onSuccess: invalidate,
    })
}
